import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap, MarkerF, PolygonF } from "@react-google-maps/api";
import DataTable from "../components/common/DataTable";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications"; // UPDATED IMPORTS
import { useDebounce } from "../hook/useDebounce";
import { useGoogleMaps } from "../context/GoogleMapsContext"; 
import { Icon } from "@iconify/react";

const LocationsGooglePage = () => {
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps(); 

  // Table & Filter States
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  // Modal & Preview States
  const [showPreview, setShowPreview] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [boundaryData, setBoundaryData] = useState(null);

  const debouncedSearch = useDebounce(search, 500);

  // 1. Fetch Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/google-places", {
        params: { 
          search: debouncedSearch, 
          page, 
          limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction 
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      showToast("error", "Failed to load Google Places"); // UPDATED
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 2. Handle Action & Goal Updates (PATCH)
  const handleUpdateStatus = async (id, field, value) => {
    try {
      await api.patch(`/google-places/${id}`, { [field]: value });
      showToast("success", `${field.replace('_', ' ')} updated`); // UPDATED
      setData(prev => prev.map(item => item.place_id === id ? { ...item, [field]: value } : item));
    } catch (error) {
      showToast("error", "Update failed"); // UPDATED
    }
  };

  // 3. Map Preview Logic
  const handlePreview = async (row) => {
    setSelectedRow(row);
    setShowPreview(true);
    setBoundaryData(null);

    if (row.referring_top_location) {
      try {
        const res = await api.get(`/top-locations/${row.referring_top_location}`);
        let area = res.data;
        if (typeof area.geometrics_outline === 'string') {
          area.geometrics_outline = JSON.parse(area.geometrics_outline);
        }
        setBoundaryData(area.geometrics_outline);
      } catch (err) {
        console.warn("No boundary found for this location");
      }
    }
  };

  // 4. Delete Logic with SweetAlert Confirmation
  const handleDelete = useCallback(async (row) => {
    // REPLACED window.confirm with confirmDelete helper
    confirmDelete(row.name, async () => {
      try {
        await api.delete(`/google-places/${row.place_id}`);
        showToast("success", "Deleted successfully"); // UPDATED
        fetchData();
      } catch (error) {
        showToast("error", "Delete failed"); // UPDATED
      }
    });
  }, [fetchData]);

  // 5. Columns Configuration
  const columns = useMemo(() => [
    { 
      key: "name", 
      label: "Name", 
      className: "fw-bold text-primary-light", 
      sortable: true 
    },
    { 
      key: "business_category", 
      label: "Category",
      render: (row) => <span className="text-secondary-light small">{row.business_category || 'N/A'}</span>
    },
    {
      key: "action_status",
      label: "Action",
      render: (row) => (
        <select 
          className="form-select form-select-sm radius-8"
          value={row.action_status || ""}
          onChange={(e) => handleUpdateStatus(row.place_id, "action_status", e.target.value)}
        >
          <option value="">Select Action</option>
          <option value="Claim">Claim</option>
          <option value="Ignore">Ignore</option>
        </select>
      )
    },
    {
      key: "goal",
      label: "Goal",
      render: (row) => (
        <select 
          className="form-select form-select-sm radius-8"
          value={row.goal || ""}
          onChange={(e) => handleUpdateStatus(row.place_id, "goal", e.target.value)}
        >
          <option value="">Select Goal</option>
          <option value="Own">Own</option>
          <option value="Deactivate">Deactivate</option>
          <option value="Move Out">Move out of Area</option>
        </select>
      )
    },
    {
      key: "preview",
      label: "Preview",
      render: (row) => (
        <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={() => handlePreview(row)}>
          <i className="ri-map-pin-line"></i> Preview
        </button>
      )
    }
  ], []);

  const tableActions = useMemo(() => [
    {
      icon: "ri-edit-line", 
      className: "text-success-main",
      onClick: (row) => navigate(`/google-places/${row.place_id}/edit`),
    },
    {
      icon: "ri-delete-bin-line", 
      className: "text-danger-main",
      onClick: handleDelete,
    },
  ], [navigate, handleDelete]);

  return (
    <>
      <DataTable
        title="Google Places Management"
        data={data}
        loading={loading}
        searchTerm={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        showCheckbox={false}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        columns={columns}
        actions={tableActions}
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
      />

      {/* MAP PREVIEW MODAL */}
      {showPreview && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content radius-16 overflow-hidden border-0 shadow-lg">
              <div className="modal-header bg-white p-20 border-bottom">
                <div>
                  <h5 className="modal-title fw-bold mb-0">{selectedRow?.name}</h5>
                  <small className="text-muted">{selectedRow?.formatted_address}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowPreview(false)}></button>
              </div>
              <div className="modal-body p-0" style={{ height: '550px', display: 'flex' }}>
                {/* Info Sidebar */}
                <div style={{ width: '280px', borderRight: '1px solid #eee', padding: '20px', overflowY: 'auto', backgroundColor: '#fff' }}>
                  <h6 className="fw-bold small text-uppercase text-primary mb-12">Details</h6>
                  <p className="small mb-4"><strong>Rating:</strong> ★ {selectedRow?.rating}</p>
                  <p className="small mb-4"><strong>Status:</strong> {selectedRow?.business_status}</p>
                  <p className="small mb-12"><strong>Phone:</strong> {selectedRow?.phone_number || 'N/A'}</p>
                  {selectedRow?.website_uri && (
                    <a href={selectedRow.website_uri} target="_blank" rel="noreferrer" className="btn btn-xs btn-primary w-100 text-white">Visit Website</a>
                  )}
                </div>

                {/* Map View */}
                <div style={{ flex: 1, position: 'relative', backgroundColor: '#f8f9fa' }}>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={{ lat: parseFloat(selectedRow.location_lat), lng: parseFloat(selectedRow.location_lng) }}
                      zoom={17}
                      options={{ disableDefaultUI: true, zoomControl: true }}
                    >
                      <MarkerF position={{ lat: parseFloat(selectedRow.location_lat), lng: parseFloat(selectedRow.location_lng) }} />
                      
                      {boundaryData && boundaryData.map((item, i) => {
                        const path = Array.isArray(item) ? item : item.path;
                        return (
                          <PolygonF 
                            key={i} 
                            paths={path} 
                            options={{ fillColor: "#0d6efd", fillOpacity: 0.1, strokeColor: "#0d6efd", strokeWeight: 2 }} 
                          />
                        );
                      })}
                    </GoogleMap>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 flex-column">
                      <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-3xl mb-2" />
                      <span>Loading Map Engine...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationsGooglePage;